import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, Autocomplete,
  FormControlLabel, Switch, Badge
} from '@mui/material';
import {
  DirectionsBus, Add, Edit, Delete, Search, Refresh,
  Warning, CheckCircle, Build, Route, People, BarChart,
  LocationOn, Schedule, Phone, Person, FileDownload,
  Report, Engineering, SyncAlt
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const VEHICLE_TYPES  = ['bus','minibus','van','car','other'];
const FUEL_TYPES     = ['diesel','petrol','electric','hybrid'];
const CONDITIONS     = ['excellent','good','fair','poor'];
const V_STATUSES     = ['active','maintenance','retired'];
const SERVICE_TYPES  = ['routine','repair','inspection','tyre','brakes','engine','bodywork','other'];
const INCIDENT_TYPES = ['breakdown','accident','delay','discipline','absence','other'];

const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const fmtT = t => t ? t.slice(0,5) : '—';
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase());
const fmt = n => `E${parseFloat(n||0).toFixed(2)}`;
const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt:3 }}>{children}</Box> : null;
}

// ── Compliance badge helper ────────────────────────────────────────────────────
function ComplianceBadge({ label, expiry }) {
  if (!expiry) return <Chip label={label + ': N/A'} size="small" color="default" sx={{ fontSize:9 }} />;
  const d = daysUntil(expiry);
  const expired = d < 0;
  const soon = d <= 30;
  const icon = expired ? ' ✗' : soon ? ' !' : ' ✓';
  const chipLabel = label + icon;
  const tooltipSuffix = expired ? ' (EXPIRED)' : soon ? ' (expires in ' + d + ' days)' : '';
  const tooltipTitle = label + ': ' + fmtD(expiry) + tooltipSuffix;
  return (
    <Tooltip title={tooltipTitle}>
      <Chip label={chipLabel} size="small"
        color={expired ? 'error' : soon ? 'warning' : 'success'} sx={{ fontSize:9 }} />
    </Tooltip>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    setLoading(true);
    api.get('/transport/stats', { params: { academic_year_id: selectedYear || undefined } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant:'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  if (loading) return <CircularProgress />;
  if (!stats) return null;
  const { summary: s, by_route, compliance_alerts, upcoming_service } = stats;

  return (
    <Box>
      <Box sx={{ mb:3, display:'flex', gap:2, alignItems:'center' }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth:200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current?' ✓':''}</MenuItem>)}
        </TextField>
      </Box>

      {(compliance_alerts?.length > 0 || s?.open_incidents > 0) && (
        <Alert severity="error" sx={{ mb:2 }}>
          <strong>Action required: </strong>
          {compliance_alerts?.length > 0 && `${compliance_alerts.length} vehicle compliance issue(s). `}
          {s?.open_incidents > 0 && `${s.open_incidents} open incident(s) need resolution.`}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb:3 }}>
        {[
          { label:'Active Vehicles',   value: s?.active_vehicles||0,    color:'#0f2b4a' },
          { label:'In Maintenance',    value: s?.in_maintenance||0,     color:'#e65100' },
          { label:'Active Routes',     value: s?.active_routes||0,      color:'#2e7d32' },
          { label:'Students on Bus',   value: s?.students_on_bus||0,    color:'#1565c0' },
          { label:'Compliance Alerts', value: compliance_alerts?.length||0, color:'#c62828' },
          { label:'Open Incidents',    value: s?.open_incidents||0,     color:'#c62828' },
        ].map(card => (
          <Grid item xs={6} sm={2} key={card.label}>
            <Card variant="outlined" sx={{ borderRadius:2, borderLeft:`4px solid ${card.color}` }}>
              <CardContent sx={{ pb:'12px !important' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{card.label}</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color:card.color, mt:0.3 }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {/* Route occupancy */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius:2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>🚌 Route Occupancy</Typography>
              {!by_route?.length && <Typography fontSize={12} color="text.secondary">No routes configured</Typography>}
              {by_route?.map((r, i) => {
                const pct = r.capacity > 0 ? Math.round((r.students / r.capacity) * 100) : 0;
                return (
                  <Box key={i} sx={{ mb:1.5 }}>
                    <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.3 }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>{r.name}</Typography>
                        <Typography fontSize={10} color="text.secondary">
                          {r.registration||'No vehicle'} · Driver: {r.driver_name||'—'} · Departs {fmtT(r.departure_time)}
                        </Typography>
                      </Box>
                      <Typography fontSize={12} fontWeight={700}>{r.students}/{r.capacity||'?'}</Typography>
                    </Box>
                    {r.capacity > 0 && (
                      <LinearProgress variant="determinate" value={Math.min(pct,100)}
                        color={pct>=100?'error':pct>=80?'warning':'success'}
                        sx={{ height:7, borderRadius:4 }} />
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ mt:0.3 }}>
                      <Chip label={`${r.morning_riders} AM`} size="small" sx={{ fontSize:9, height:16 }} />
                      <Chip label={`${r.afternoon_riders} PM`} size="small" sx={{ fontSize:9, height:16 }} />
                      {pct >= 100 && <Chip label="FULL" size="small" color="error" sx={{ fontSize:9, height:16, fontWeight:700 }} />}
                    </Stack>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance + maintenance */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {compliance_alerts?.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius:2, border:'1px solid #fca5a5' }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom color="error">⚠ Compliance Alerts</Typography>
                  {compliance_alerts.map((a, i) => (
                    <Box key={i} sx={{ display:'flex', justifyContent:'space-between', py:0.5, borderBottom:'1px solid #f5f5f5' }}>
                      <Box>
                        <Typography fontSize={12} fontWeight={600}>{a.registration}</Typography>
                        <Typography fontSize={10} color="text.secondary">{a.make} {a.model}</Typography>
                      </Box>
                      <Chip label={ptLabel(a.alert_type?.replace('_',' '))} size="small"
                        color={a.alert_type?.includes('EXPIRED')?'error':'warning'} sx={{ fontSize:10, fontWeight:700 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
            {upcoming_service?.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius:2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>🔧 Service Due (14 days)</Typography>
                  {upcoming_service.map((s, i) => (
                    <Box key={i} sx={{ display:'flex', justifyContent:'space-between', py:0.5 }}>
                      <Box>
                        <Typography fontSize={12} fontWeight={600}>{s.registration}</Typography>
                        <Typography fontSize={10} color="text.secondary">{s.description}</Typography>
                      </Box>
                      <Chip label={fmtD(s.next_service_date)} size="small" color="warning" sx={{ fontSize:10 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Vehicles Tab ───────────────────────────────────────────────────────────────
function VehiclesTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [dialog, setDialog]     = useState(false);
  const [editing, setEditing]   = useState(null);
  const [maintDialog, setMaintDialog] = useState(null);

  const BLANK = { registration:'', make:'', model:'', year:'', capacity:50, vehicle_type:'bus', colour:'',
    licence_expiry:'', roadworthy_expiry:'', insurance_expiry:'', status:'active', condition:'good', fuel_type:'diesel', notes:'' };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const MBLANK = { vehicle_id:'', service_type:'routine', description:'', service_date: new Date().toISOString().split('T')[0],
    next_service_date:'', odometer_km:'', cost:'', workshop:'', status:'completed' };
  const [maintForm, setMaintForm] = useState(MBLANK);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/transport/vehicles'); if (r.data.success) setVehicles(r.data.data); }
    catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.registration || !form.capacity) return enqueueSnackbar('Registration and capacity required', { variant:'warning' });
    try {
      if (editing) { await api.put(`/transport/vehicles/${editing.id}`, form); enqueueSnackbar('Updated', { variant:'success' }); }
      else { await api.post('/transport/vehicles', form); enqueueSnackbar('Vehicle registered', { variant:'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this vehicle?')) return;
    try { await api.delete(`/transport/vehicles/${id}`); enqueueSnackbar('Removed', { variant:'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const saveMaint = async () => {
    try {
      await api.post('/transport/maintenance', { ...maintForm, vehicle_id: maintDialog.id });
      enqueueSnackbar('Service record added', { variant:'success' });
      setMaintDialog(null); load();
    } catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb:2 }} justifyContent="flex-end">
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('vehicles', ['Reg','Make','Model','Type','Capacity','Status','Licence','Roadworthy','Insurance'],
            vehicles.map(v => [v.registration, v.make||'', v.model||'', v.vehicle_type, v.capacity, v.status, fmtD(v.licence_expiry), fmtD(v.roadworthy_expiry), fmtD(v.insurance_expiry)]))
        }>Export</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({...BLANK}); setDialog(true); }} sx={{ bgcolor:'#0f2b4a' }}>
          Register Vehicle
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {vehicles.length === 0 && (
            <Grid item xs={12}><Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}>
              <DirectionsBus sx={{ fontSize:56, color:'text.disabled', mb:1 }} />
              <Typography color="text.secondary">No vehicles registered. Click "Register Vehicle" to add your first bus.</Typography>
            </Paper></Grid>
          )}
          {vehicles.map(v => {
            const licDays  = daysUntil(v.licence_expiry);
            const rwDays   = daysUntil(v.roadworthy_expiry);
            const insDays  = daysUntil(v.insurance_expiry);
            const alert    = [licDays, rwDays, insDays].some(d => d !== null && d < 30);
            return (
              <Grid item xs={12} sm={6} md={4} key={v.id}>
                <Card variant="outlined" sx={{ borderRadius:2, border: alert?'1px solid #fca5a5':undefined,
                  borderLeft:`4px solid ${v.status==='active'?'#2e7d32':v.status==='maintenance'?'#e65100':'#888'}` }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb:1 }}>
                      <Box>
                        <Typography fontWeight={700} fontSize={16} fontFamily="monospace">{v.registration}</Typography>
                        <Typography fontSize={12} color="text.secondary">{[v.year, v.make, v.model].filter(Boolean).join(' ')} · {ptLabel(v.vehicle_type)}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={ptLabel(v.status)} size="small" color={v.status==='active'?'success':v.status==='maintenance'?'warning':'default'} sx={{ fontWeight:700 }} />
                      </Stack>
                    </Stack>

                    <Grid container spacing={1} sx={{ mb:1 }}>
                      <Grid item xs={6}><Typography fontSize={9} color="text.secondary">CAPACITY</Typography><Typography fontWeight={700}>{v.capacity} seats</Typography></Grid>
                      <Grid item xs={6}><Typography fontSize={9} color="text.secondary">STUDENTS</Typography><Typography fontWeight={700}>{v.student_count||0} assigned</Typography></Grid>
                    </Grid>

                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb:1 }}>
                      <ComplianceBadge label="Lic"  expiry={v.licence_expiry} />
                      <ComplianceBadge label="RW"   expiry={v.roadworthy_expiry} />
                      <ComplianceBadge label="Ins"  expiry={v.insurance_expiry} />
                    </Stack>

                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Log service">
                        <IconButton size="small" color="warning" onClick={() => { setMaintDialog(v); setMaintForm({...MBLANK, vehicle_id:v.id}); }}>
                          <Build fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => { setEditing(v); setForm({ registration:v.registration, make:v.make||'', model:v.model||'', year:v.year||'', capacity:v.capacity, vehicle_type:v.vehicle_type, colour:v.colour||'', licence_expiry:v.licence_expiry?.split('T')[0]||'', roadworthy_expiry:v.roadworthy_expiry?.split('T')[0]||'', insurance_expiry:v.insurance_expiry?.split('T')[0]||'', status:v.status, condition:v.condition, fuel_type:v.fuel_type, notes:v.notes||'' }); setDialog(true); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => del(v.id)}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Vehicle dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>{editing ? 'Edit Vehicle' : 'Register Vehicle'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={12}><Typography variant="caption" fontWeight={700} color="primary">IDENTIFICATION</Typography></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Registration *" value={form.registration} onChange={setF('registration')} placeholder="e.g. SD 1234 AB" disabled={!!editing} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Type" value={form.vehicle_type} onChange={setF('vehicle_type')}>
                {VEHICLE_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Make" value={form.make} onChange={setF('make')} placeholder="e.g. Toyota" /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Model" value={form.model} onChange={setF('model')} placeholder="e.g. Coaster" /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Year" value={form.year} onChange={setF('year')} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Capacity *" value={form.capacity} onChange={setF('capacity')} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Colour" value={form.colour} onChange={setF('colour')} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Fuel" value={form.fuel_type} onChange={setF('fuel_type')}>
                {FUEL_TYPES.map(f => <MenuItem key={f} value={f}>{ptLabel(f)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Condition" value={form.condition} onChange={setF('condition')}>
                {CONDITIONS.map(c => <MenuItem key={c} value={c}>{ptLabel(c)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mt:1 }}>COMPLIANCE DATES</Typography></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Licence Expiry" value={form.licence_expiry} onChange={setF('licence_expiry')} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Roadworthy Expiry" value={form.roadworthy_expiry} onChange={setF('roadworthy_expiry')} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Insurance Expiry" value={form.insurance_expiry} onChange={setF('insurance_expiry')} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Status" value={form.status} onChange={setF('status')}>
                {V_STATUSES.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={form.notes} onChange={setF('notes')} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor:'#0f2b4a' }}>{editing ? 'Update' : 'Register'}</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance log dialog */}
      <Dialog open={!!maintDialog} onClose={() => setMaintDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Service — {maintDialog?.registration}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Service Type" value={maintForm.service_type} onChange={e => setMaintForm(f => ({ ...f, service_type:e.target.value }))}>
                {SERVICE_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Status" value={maintForm.status} onChange={e => setMaintForm(f => ({ ...f, status:e.target.value }))}>
                {['scheduled','in_progress','completed','cancelled'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description *" value={maintForm.description} onChange={e => setMaintForm(f => ({ ...f, description:e.target.value }))} multiline rows={2} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Service Date" value={maintForm.service_date} onChange={e => setMaintForm(f => ({ ...f, service_date:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Next Service Due" value={maintForm.next_service_date} onChange={e => setMaintForm(f => ({ ...f, next_service_date:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Odometer (km)" value={maintForm.odometer_km} onChange={e => setMaintForm(f => ({ ...f, odometer_km:e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Cost (E)" value={maintForm.cost} onChange={e => setMaintForm(f => ({ ...f, cost:e.target.value }))} InputProps={{ startAdornment:<InputAdornment position="start">E</InputAdornment> }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Workshop / Provider" value={maintForm.workshop} onChange={e => setMaintForm(f => ({ ...f, workshop:e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={saveMaint}>Save Service Record</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Routes Tab ─────────────────────────────────────────────────────────────────
function RoutesTab({ academicYears, students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [routes, setRoutes]       = useState([]);
  const [vehicles, setVehicles]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [detailId, setDetailId]   = useState(null);
  const [detail, setDetail]       = useState(null);
  const [stopDialog, setStopDialog] = useState(null);
  const [allocDialog, setAllocDialog] = useState(null);
  const [selectedYear, setYear]   = useState('');

  const BLANK_ROUTE = { name:'', code:'', direction:'both', vehicle_id:'', driver_name:'', driver_phone:'',
    assistant_name:'', assistant_phone:'', departure_time:'', arrival_time:'', afternoon_depart:'',
    distance_km:'', fare_amount:0, notes:'' };
  const BLANK_STOP = { route_id:'', stop_name:'', stop_order:1, pickup_time:'', dropoff_time:'', landmark:'' };
  const BLANK_ALLOC = { student_id:'', route_id:'', stop_id:'', academic_year_id:'', uses_morning:true, uses_afternoon:true, notes:'' };

  const [routeForm, setRouteForm] = useState(BLANK_ROUTE);
  const [stopForm, setStopForm]   = useState(BLANK_STOP);
  const [allocForm, setAllocForm] = useState(BLANK_ALLOC);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rr, vr] = await Promise.all([api.get('/transport/routes'), api.get('/transport/vehicles', { params:{status:'active'} })]);
      if (rr.data.success) setRoutes(rr.data.data);
      if (vr.data.success) setVehicles(vr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const openDetail = async (id) => {
    setDetailId(id); setDetail(null);
    const r = await api.get(`/transport/routes/${id}`);
    if (r.data.success) setDetail(r.data.data);
  };

  const saveRoute = async () => {
    try {
      if (editing) { await api.put(`/transport/routes/${editing.id}`, routeForm); enqueueSnackbar('Updated', { variant:'success' }); }
      else { await api.post('/transport/routes', routeForm); enqueueSnackbar('Route created', { variant:'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const saveStop = async () => {
    try {
      const isEdit = stopForm.id;
      if (isEdit) { await api.put(`/transport/stops/${stopForm.id}`, stopForm); }
      else { await api.post('/transport/stops', { ...stopForm, route_id: detailId }); }
      enqueueSnackbar('Stop saved', { variant:'success' });
      setStopDialog(null); openDetail(detailId);
    } catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const deleteStop = async (id) => {
    if (!window.confirm('Remove this stop?')) return;
    try { await api.delete(`/transport/stops/${id}`); openDetail(detailId); }
    catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  const saveAlloc = async () => {
    try {
      await api.post('/transport/allocations', { ...allocForm, route_id: detailId, academic_year_id: selectedYear });
      enqueueSnackbar('Student allocated', { variant:'success' });
      setAllocDialog(null); openDetail(detailId);
    } catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const removeAlloc = async (id) => {
    if (!window.confirm('Remove this student from the route?')) return;
    try { await api.delete(`/transport/allocations/${id}`); openDetail(detailId); }
    catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  const delRoute = async (id) => {
    if (!window.confirm('Delete this route?')) return;
    try { await api.delete(`/transport/routes/${id}`); enqueueSnackbar('Deleted', { variant:'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const detailStops = detail?.stops || [];

  return (
    <Box>
      <Stack direction="row" sx={{ mb:2 }} justifyContent="flex-end" spacing={1}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth:180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current?' ✓':''}</MenuItem>)}
        </TextField>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setRouteForm({...BLANK_ROUTE}); setDialog(true); }} sx={{ bgcolor:'#0f2b4a' }}>
          Add Route
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {routes.length === 0 && (
            <Grid item xs={12}><Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}>
              <Route sx={{ fontSize:56, color:'text.disabled', mb:1 }} />
              <Typography color="text.secondary">No routes yet. Click "Add Route" to create your first bus route.</Typography>
            </Paper></Grid>
          )}
          {routes.map(r => (
            <Grid item xs={12} sm={6} key={r.id}>
              <Card variant="outlined" sx={{ borderRadius:2, cursor:'pointer' }} onClick={() => openDetail(r.id)}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flexGrow:1, mr:1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography fontWeight={700} fontSize={14}>{r.name}</Typography>
                        {r.code && <Chip label={r.code} size="small" sx={{ fontFamily:'monospace', fontSize:10 }} />}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt:0.3 }}>
                        <Typography fontSize={11} color="text.secondary">{r.registration||'No vehicle'}</Typography>
                        {r.departure_time && <Typography fontSize={11} color="text.secondary">· Dep {fmtT(r.departure_time)}</Typography>}
                        {r.arrival_time && <Typography fontSize={11} color="text.secondary">→ Arr {fmtT(r.arrival_time)}</Typography>}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => { setEditing(r); setRouteForm({ name:r.name, code:r.code||'', direction:r.direction, vehicle_id:r.vehicle_id||'', driver_name:r.driver_name||'', driver_phone:r.driver_phone||'', assistant_name:r.assistant_name||'', assistant_phone:r.assistant_phone||'', departure_time:r.departure_time||'', arrival_time:r.arrival_time||'', afternoon_depart:r.afternoon_depart||'', distance_km:r.distance_km||'', fare_amount:r.fare_amount||0, notes:r.notes||'' }); setDialog(true); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => delRoute(r.id)}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                  <Divider sx={{ my:1 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={4}><Typography fontSize={9} color="text.secondary">STOPS</Typography><Typography fontWeight={700}>{r.stop_count}</Typography></Grid>
                    <Grid item xs={4}><Typography fontSize={9} color="text.secondary">STUDENTS</Typography><Typography fontWeight={700}>{r.student_count}</Typography></Grid>
                    <Grid item xs={4}><Typography fontSize={9} color="text.secondary">CAPACITY</Typography><Typography fontWeight={700}>{r.vehicle_capacity||'—'}</Typography></Grid>
                  </Grid>
                  {r.driver_name && <Typography fontSize={11} color="text.secondary" sx={{ mt:0.5 }}>Driver: {r.driver_name} {r.driver_phone?`· ${r.driver_phone}`:''}</Typography>}
                  {r.fare_amount > 0 && <Typography fontSize={11} color="text.secondary">Fare: {fmt(r.fare_amount)}/term</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Route detail dialog */}
      <Dialog open={!!detailId} onClose={() => { setDetailId(null); setDetail(null); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={700}>{detail?.name}</Typography>
              <Typography fontSize={12} sx={{ opacity:0.7 }}>{detail?.registration||'No vehicle'} · {detail?.stop_count||0} stops · {detail?.students?.length||0} students</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {!detail ? <Box sx={{ p:3, textAlign:'center' }}><CircularProgress /></Box> : (
            <Grid container spacing={2}>
              {/* Stops */}
              <Grid item xs={12} md={5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
                  <Typography fontWeight={700} fontSize={13}>🚏 Stops (in order)</Typography>
                  <Button size="small" startIcon={<Add />} onClick={() => { setStopForm({ ...BLANK_STOP, stop_order: (detail.stops?.length||0)+1 }); setStopDialog('new'); }}>Add Stop</Button>
                </Stack>
                {detail.stops?.length === 0 && <Typography fontSize={12} color="text.secondary">No stops yet</Typography>}
                {detail.stops?.map((s, i) => (
                  <Paper key={s.id} variant="outlined" sx={{ p:1, mb:0.8, borderRadius:1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip label={s.stop_order} size="small" sx={{ minWidth:24, fontWeight:700, fontSize:10 }} />
                          <Typography fontSize={12} fontWeight={600}>{s.stop_name}</Typography>
                        </Stack>
                        {s.landmark && <Typography fontSize={10} color="text.secondary">{s.landmark}</Typography>}
                        <Stack direction="row" spacing={1}>
                          {s.pickup_time && <Typography fontSize={10} color="primary.main">↑ {fmtT(s.pickup_time)}</Typography>}
                          {s.dropoff_time && <Typography fontSize={10} color="error.main">↓ {fmtT(s.dropoff_time)}</Typography>}
                        </Stack>
                      </Box>
                      <Stack direction="row">
                        <IconButton size="small" onClick={() => { setStopForm({...s, pickup_time:s.pickup_time||'', dropoff_time:s.dropoff_time||''}); setStopDialog('edit'); }}><Edit sx={{ fontSize:14 }} /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteStop(s.id)}><Delete sx={{ fontSize:14 }} /></IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Grid>

              {/* Students */}
              <Grid item xs={12} md={7}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
                  <Typography fontWeight={700} fontSize={13}>👥 Students ({detail.students?.length||0})</Typography>
                  <Button size="small" startIcon={<Add />} onClick={() => { setAllocForm({...BLANK_ALLOC}); setAllocDialog(true); }}>Add Student</Button>
                </Stack>
                <TableContainer sx={{ maxHeight:380 }}>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor:'#f5f6fa' }}>
                      {['Student','Class','Stop','AM','PM',''].map(h => <TableCell key={h} sx={{ fontWeight:700, fontSize:11 }}>{h}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {detail.students?.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py:3, color:'text.secondary' }}>No students allocated</TableCell></TableRow>}
                      {detail.students?.map((s, i) => (
                        <TableRow key={s.id} hover sx={{ bgcolor: i%2===0?'white':'#fafafa' }}>
                          <TableCell>
                            <Typography fontSize={12} fontWeight={600}>{s.last_name}, {s.first_name}</Typography>
                            <Typography fontSize={10} color="text.secondary">{s.admission_number}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize:11 }}>{s.class_name||'—'}</TableCell>
                          <TableCell sx={{ fontSize:11 }}>{s.stop_name||'—'}</TableCell>
                          <TableCell align="center">{s.uses_morning?<CheckCircle sx={{ fontSize:14, color:'green' }} />:'—'}</TableCell>
                          <TableCell align="center">{s.uses_afternoon?<CheckCircle sx={{ fontSize:14, color:'green' }} />:'—'}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => removeAlloc(s.id)}><Delete sx={{ fontSize:14 }} /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailId(null); setDetail(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Route form dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>{editing ? 'Edit Route' : 'Add Route'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={8}><TextField fullWidth size="small" label="Route Name *" value={routeForm.name} onChange={e => setRouteForm(f => ({ ...f, name:e.target.value }))} placeholder="e.g. Northern Route — Mbabane" /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Code" value={routeForm.code} onChange={e => setRouteForm(f => ({ ...f, code:e.target.value }))} placeholder="NR1" /></Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Vehicle" value={routeForm.vehicle_id} onChange={e => setRouteForm(f => ({ ...f, vehicle_id:e.target.value }))}>
                <MenuItem value="">No vehicle assigned</MenuItem>
                {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{v.registration} — {v.make} {v.model} ({v.capacity} seats)</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Driver Name" value={routeForm.driver_name} onChange={e => setRouteForm(f => ({ ...f, driver_name:e.target.value }))} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Driver Phone" value={routeForm.driver_phone} onChange={e => setRouteForm(f => ({ ...f, driver_phone:e.target.value }))} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Bus Monitor / Attendant" value={routeForm.assistant_name} onChange={e => setRouteForm(f => ({ ...f, assistant_name:e.target.value }))} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Monitor Phone" value={routeForm.assistant_phone} onChange={e => setRouteForm(f => ({ ...f, assistant_phone:e.target.value }))} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="time" label="Morning Depart" value={routeForm.departure_time} onChange={e => setRouteForm(f => ({ ...f, departure_time:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="time" label="Arrive School" value={routeForm.arrival_time} onChange={e => setRouteForm(f => ({ ...f, arrival_time:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="time" label="Afternoon Depart" value={routeForm.afternoon_depart} onChange={e => setRouteForm(f => ({ ...f, afternoon_depart:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Distance (km)" value={routeForm.distance_km} onChange={e => setRouteForm(f => ({ ...f, distance_km:e.target.value }))} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="number" label="Fare per Term (E)" value={routeForm.fare_amount} onChange={e => setRouteForm(f => ({ ...f, fare_amount:e.target.value }))} InputProps={{ startAdornment:<InputAdornment position="start">E</InputAdornment> }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={routeForm.notes} onChange={e => setRouteForm(f => ({ ...f, notes:e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRoute} sx={{ bgcolor:'#0f2b4a' }}>{editing ? 'Update' : 'Create Route'}</Button>
        </DialogActions>
      </Dialog>

      {/* Stop dialog */}
      <Dialog open={!!stopDialog} onClose={() => setStopDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{stopDialog==='edit' ? 'Edit Stop' : 'Add Stop'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={8}><TextField fullWidth size="small" label="Stop Name *" value={stopForm.stop_name} onChange={e => setStopForm(f => ({ ...f, stop_name:e.target.value }))} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Order" value={stopForm.stop_order} onChange={e => setStopForm(f => ({ ...f, stop_order:parseInt(e.target.value)||1 }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Landmark" value={stopForm.landmark||''} onChange={e => setStopForm(f => ({ ...f, landmark:e.target.value }))} placeholder="Near the post office" /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="time" label="Morning Pick-up" value={stopForm.pickup_time||''} onChange={e => setStopForm(f => ({ ...f, pickup_time:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="time" label="Afternoon Drop-off" value={stopForm.dropoff_time||''} onChange={e => setStopForm(f => ({ ...f, dropoff_time:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveStop} sx={{ bgcolor:'#0f2b4a' }}>Save Stop</Button>
        </DialogActions>
      </Dialog>

      {/* Allocate student dialog */}
      <Dialog open={!!allocDialog} onClose={() => setAllocDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Allocate Student to Route</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={students}
                getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                onChange={(_, v) => setAllocForm(f => ({ ...f, student_id: v?.id||'' }))}
                renderInput={p => <TextField {...p} label="Student *" />} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Pick-up / Drop-off Stop" value={allocForm.stop_id} onChange={e => setAllocForm(f => ({ ...f, stop_id:e.target.value }))}>
                <MenuItem value="">No specific stop</MenuItem>
                {detailStops.map(s => <MenuItem key={s.id} value={s.id}>{s.stop_order}. {s.stop_name}{s.pickup_time?` (${fmtT(s.pickup_time)})`:''}  </MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel control={<Switch size="small" checked={!!allocForm.uses_morning} onChange={e => setAllocForm(f => ({ ...f, uses_morning:e.target.checked }))} />}
                  label={<Typography fontSize={13}>Uses morning bus</Typography>} />
                <FormControlLabel control={<Switch size="small" checked={!!allocForm.uses_afternoon} onChange={e => setAllocForm(f => ({ ...f, uses_afternoon:e.target.checked }))} />}
                  label={<Typography fontSize={13}>Uses afternoon bus</Typography>} />
              </Stack>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={allocForm.notes} onChange={e => setAllocForm(f => ({ ...f, notes:e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveAlloc} sx={{ bgcolor:'#0f2b4a' }}>Allocate Student</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Incidents Tab ──────────────────────────────────────────────────────────────
function IncidentsTab({ students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [incidents, setIncidents] = useState([]);
  const [routes, setRoutes]       = useState([]);
  const [vehicles, setVehicles]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterRoute, setRoute]   = useState('');
  const [dialog, setDialog]       = useState(false);
  const [form, setForm] = useState({ route_id:'', vehicle_id:'', student_id:'', incident_date: new Date().toISOString().split('T')[0], incident_type:'other', description:'', action_taken:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ir, rr, vr] = await Promise.all([
        api.get('/transport/incidents', { params: { route_id: filterRoute||undefined } }),
        api.get('/transport/routes'),
        api.get('/transport/vehicles')
      ]);
      if (ir.data.success) setIncidents(ir.data.data);
      if (rr.data.success) setRoutes(rr.data.data);
      if (vr.data.success) setVehicles(vr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [filterRoute, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await api.post('/transport/incidents', form); enqueueSnackbar('Recorded', { variant:'success' }); setDialog(false); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  const resolve = async (id) => {
    try { await api.put(`/transport/incidents/${id}/resolve`); enqueueSnackbar('Resolved', { variant:'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  const ITYPE_COLOR = { breakdown:'error', accident:'error', delay:'warning', discipline:'warning', absence:'default', other:'default' };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb:2 }} alignItems="center">
        <TextField select size="small" label="Route" value={filterRoute} onChange={e => setRoute(e.target.value)} sx={{ minWidth:200 }}>
          <MenuItem value="">All routes</MenuItem>
          {routes.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </TextField>
        <Chip label={`${incidents.filter(i=>!i.resolved).length} open`} size="small" color="warning" />
        <Box sx={{ flexGrow:1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)} sx={{ bgcolor:'#0f2b4a' }}>Log Incident</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius:2 }}>
          <TableContainer sx={{ maxHeight:500 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>
                {['Date','Route','Type','Description','Student','Action','Status',''].map(h => (
                  <TableCell key={h} sx={{ bgcolor:'#0f2b4a', color:'white', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {incidents.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py:4, color:'text.secondary' }}>No incidents recorded</TableCell></TableRow>}
                {incidents.map((inc, i) => (
                  <TableRow key={inc.id} hover sx={{ bgcolor: !inc.resolved?'#fffde7': i%2===0?'white':'#fafafa' }}>
                    <TableCell sx={{ fontSize:11 }}>{fmtD(inc.incident_date)}</TableCell>
                    <TableCell sx={{ fontSize:11 }}>{inc.route_name}</TableCell>
                    <TableCell><Chip label={ptLabel(inc.incident_type)} size="small" color={ITYPE_COLOR[inc.incident_type]||'default'} sx={{ fontSize:10 }} /></TableCell>
                    <TableCell sx={{ fontSize:11, maxWidth:200 }}>{inc.description}</TableCell>
                    <TableCell sx={{ fontSize:11 }}>{inc.first_name ? `${inc.first_name} ${inc.last_name}` : '—'}</TableCell>
                    <TableCell sx={{ fontSize:11 }}>{inc.action_taken||'—'}</TableCell>
                    <TableCell><Chip label={inc.resolved?'Resolved':'Open'} size="small" color={inc.resolved?'success':'warning'} sx={{ fontSize:10 }} /></TableCell>
                    <TableCell>
                      {!inc.resolved && <Button size="small" variant="outlined" color="success" onClick={() => resolve(inc.id)} sx={{ fontSize:10, py:0.3 }}>Resolve</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Transport Incident</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Route *" value={form.route_id} onChange={e => setForm(f => ({ ...f, route_id:e.target.value }))}>
                {routes.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Vehicle" value={form.vehicle_id} onChange={e => setForm(f => ({ ...f, vehicle_id:e.target.value }))}>
                <MenuItem value="">Select vehicle…</MenuItem>
                {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{v.registration}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Incident Type" value={form.incident_type} onChange={e => setForm(f => ({ ...f, incident_type:e.target.value }))}>
                {INCIDENT_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Date" value={form.incident_date} onChange={e => setForm(f => ({ ...f, incident_date:e.target.value }))} InputLabelProps={{ shrink:true }} /></Grid>
            {form.incident_type === 'discipline' && (
              <Grid item xs={12}>
                <Autocomplete size="small" options={students}
                  getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                  onChange={(_, v) => setForm(f => ({ ...f, student_id:v?.id||'' }))}
                  renderInput={p => <TextField {...p} label="Student involved" />} />
              </Grid>
            )}
            <Grid item xs={12}><TextField fullWidth size="small" label="Description *" value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))} multiline rows={3} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Action Taken" value={form.action_taken} onChange={e => setForm(f => ({ ...f, action_taken:e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor:'#0f2b4a' }}>Log Incident</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function TransportManagement() {
  const [tab, setTab]           = useState(0);
  const [academicYears, setAY]  = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    import('../services/api').then(({ studentsAPI }) => {
      studentsAPI.getAll({ limit:2000, status:'active' }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <DirectionsBus color="primary" /> Transport & Bus Routes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vehicle register · Route management · Student allocations · Compliance · Incidents
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_,v) => setTab(v)} sx={{ mb:1, borderBottom:1, borderColor:'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}        label="Overview"    iconPosition="start" sx={{ fontSize:12 }} />
        <Tab icon={<DirectionsBus fontSize="small" />}  label="Vehicles"    iconPosition="start" sx={{ fontSize:12 }} />
        <Tab icon={<Route fontSize="small" />}           label="Routes"      iconPosition="start" sx={{ fontSize:12 }} />
        <Tab icon={<Report fontSize="small" />}         label="Incidents"   iconPosition="start" sx={{ fontSize:12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><VehiclesTab /></TabPanel>
      <TabPanel value={tab} index={2}><RoutesTab academicYears={academicYears} students={students} /></TabPanel>
      <TabPanel value={tab} index={3}><IncidentsTab students={students} /></TabPanel>
    </Box>
  );
}
